import { SCROLLBAR_ORIENTATION_VERTICAL, SCROLLBAR_ORIENTATION_HORIZONTAL } from './const';

class ScrollbarDirectiveFactory {
    static directives = [];
    static scrollbars = [];

    /**
     * Создает директиву и заносит ее в массив с директивами. Если в массиве со скроллбарами
     * есть соответствующие директиве скроллбары, то регистрирует их.
     */
    static create(element, options, vnode) {
      const directive = new ScrollbarDirective(element, options, vnode);

      this.directives.push(directive);

      const { id: scrollbarId } = options;
      const directivesScrollbars = this.scrollbars.filter(({ id }) => scrollbarId === id);
      directivesScrollbars.forEach(scrollbar => {
        directive.registerScrollbar(scrollbar);

        const scrollbarIndex = this.scrollbars.indexOf(scrollbar);
        this.scrollbars.splice(scrollbarIndex, 1);
      });
    }

    static update(element, options, vnode) {
      const { directives } = this;
      const directive = directives.find(directive => directive.element === element);

      if (directive) {
        directive.update(options, vnode);
      }
    }

    /**
     * Находит директиву и уничтожает ее
     */
    static destroy(element) {
      const { directives } = this;
      const directive = directives.find(directive => directive.element === element);
      const directiveIndex = directives.indexOf(directive);

      directive.destroy();

      directives.splice(directiveIndex, 1);
    }

    /**
     * Функция вызывается из CUIScrollbar и из create фабрики. Находит соответствующую скроллбару
     * директиву, и регистрирует скроллбар в ней. Если таковой директивы нет, добавляет скроллбар
     * в массив, откуда он находится методом create этой фабрики
     */
    static registerScrollbar(scrollbar) {
      const { directives } = this;
      const directive = directives.find(({ scrollbarId }) => scrollbarId === scrollbar.id);

      if (directive) {
        directive.registerScrollbar(scrollbar);
      } else {
        this.scrollbars.push(scrollbar);
      }
    }

    /**
     * Создает интерфейс директивы, используемый Vue
     */
    static createHooks() {
      return {
        /**
         * Происходит в момент вставки элемента с директивой в DOM.
         * param {HTMLElement} element - непосредственно элемен
         * param {object} binding - нас интересует объект value, который является опциями
         * директивы
         * param {object} vnode - виртуальный элемент Vue
         */
        inserted(element, { value: options }, vnode) {
          ScrollbarDirectiveFactory.create(element, options, vnode);
        },
        update(element, { value: options }, vnode) {
          ScrollbarDirectiveFactory.update(element, options, vnode);
        },
        unbind(element) {
          ScrollbarDirectiveFactory.destroy(element);
        },
      };
    }
}

const SHIFT_CODE = 16;

class ScrollbarDirective {
  constructor(element, options, vnode) {
    // Привязываем функции к контексту экземпляра, так как нам понадобится их удалять
    this.wheelHandler = this.wheelHandler.bind(this);
    this.touchstartHandler = this.touchstartHandler.bind(this);
    this.touchendHandler = this.touchendHandler.bind(this);
    this.touchmoveHandler = this.touchmoveHandler.bind(this);
    this.keydownHandler = this.keydownHandler.bind(this);
    this.keyupHandler = this.keyupHandler.bind(this);
    this.mouseMoveHandler = this.mouseMoveHandler.bind(this);
    this.mouseUpHandler = this.mouseUpHandler.bind(this);
    this.mouseDownHandler = this.mouseDownHandler.bind(this);
    this.inertialScroll = this.inertialScroll.bind(this);
    this.averageDeltaSpeed = this.averageDeltaSpeed.bind(this);

    this.element = element;

    this.element.style.overflow = 'hidden';

    const {
      id: scrollbarId, // id привязанного к директиве скроллбара
      dynamic = true, // будет ли директива отслеживать изменение высоты элемента
      // eslint-disable-next-line no-unused-vars
      scrolling = true, // возможность скроллинга (отключает или включает)
      speed = 1, // скорость скроллинга (только для мыши)
      invertScroll = false, // Надо ли инвертить колесо с горизонтального на вертикальное передвижение
      scrollLeft, // начальное и устанавливаемое значение скролла
      scrollTop,
    } = options;

    this.scrollbarId = scrollbarId;
    this.vnode = vnode;
    this.setSpeed(speed);
    this.invertScroll = invertScroll;

    this.scrollDeltaSpeed = {
      deltaX: 0,
      deltaY: 0,
      count: 1,
    };
    this.inertionalProcessing = false;

    if (dynamic) {
      this.setSizesWatcher();
    }

    this.processInitialOverflow();

    if (scrollLeft || scrollTop) {
      this.applyScroll(scrollLeft, scrollTop);
    }
  }

  get isElementOverflown() {
    const { scrollHeight, clientHeight, scrollWidth, clientWidth } = this.element;

    return (
      scrollHeight > clientHeight || scrollWidth > clientWidth
    );
  }

  // eslint-disable-next-line no-unused-vars
  update(options, vnode) {
    this.updateDynamic(options.dynamic);
    this.updateScrolling(options.scrolling);

    const { scrollLeft, scrollTop } = options;

    if (scrollLeft !== undefined || scrollTop !== undefined) {
      const { scrollLeft: oldScrollLeft, scrollTop: oldScrollTop } = this.element;
      this.applyScroll(scrollLeft - oldScrollLeft, scrollTop - oldScrollTop);
    }
  }

  updateDynamic(dynamic) {
    if (dynamic === this.dynamic) {
      return;
    }

    this.dynamic = dynamic;

    if (dynamic) {
      this.setSizesWatcher();
    } else {
      this.removeSizesWatcher();
    }
  }

  updateScrolling(scrolling) {
    if (scrolling === this.scrolling) {
      return;
    }

    this.scrolling = scrolling;

    if (this.scrolling) {
      this.setScrollProcessing();
    } else {
      this.removeScrollProcessing();
    }
  }

  /**
   * Уничтожает директиву, удаляя все обработчики и интервалы. Вызывается из
   * ScrollbarDirectiveFactory. Также сохраняет скролл элемента, если задана такая опция
   */
  destroy() {
    this.removeSizesWatcher();
    this.removeScrollProcessing();
  }

  /**
   * Функция вызывается из ScrollbarDirectiveFactory
   */
  registerScrollbar(scrollbar) {
    const { $orientation: orientation } = scrollbar;
    const {
      scrollHeight,
      clientHeight,
      scrollWidth,
      clientWidth,
      scrollTop,
      scrollLeft,
    } = this.element;

    scrollbar.$directive = this;

    if (orientation === SCROLLBAR_ORIENTATION_VERTICAL) {
      this.verticalScrollbar = scrollbar;
      this.verticalScrollbar.render(scrollTop, scrollLeft, scrollHeight, clientHeight);
    } else if (orientation === SCROLLBAR_ORIENTATION_HORIZONTAL) {
      this.horizontalScrollbar = scrollbar;
      this.horizontalScrollbar.render(scrollLeft, scrollTop, scrollWidth, clientWidth);
    }
  }

  setSpeed(speed) {
    if (speed > 5) {
      speed = 5;
    }

    if (speed < 1) {
      speed = 1;
    }

    this.speed = speed;
  }

  /**
   * Устанавливает интервал слежения за размерами элемента и начальные размеры элемента
   */
  setSizesWatcher() {
    this.lastScrollHeight = this.element.scrollHeight;
    this.lastClientHeight = this.element.clientHeight;
    this.lastScrollWidth = this.element.scrollWidth;
    this.lastClientWidth = this.element.clientWidth;

    this.sizesWatcherInterval = setInterval(this.watchSizes.bind(this), 40);
  }

  removeSizesWatcher() {
    clearInterval(this.sizesWatcherInterval);
  }

  // eslint-disable-next-line complexity
  watchSizes() {
    const {
      scrollHeight,
      clientHeight,
      scrollWidth,
      clientWidth,
      scrollTop,
      scrollLeft,
    } = this.element;

    const isChangedVertically = scrollHeight !== this.lastScrollHeight || clientHeight !== this.lastClientHeight;
    const isChangedHorizontally = scrollWidth !== this.lastScrollWidth || clientWidth !== this.lastClientWidth;

    /**
     * Если высота элемента или его внутренностей поменялись, приводим вертикальный скроллбар
     * в соответствие
     */
    if (isChangedVertically) {
      if (this.verticalScrollbar) {
        this.verticalScrollbar.render(scrollTop, scrollLeft, scrollHeight, clientHeight);
      }

      this.lastScrollHeight = scrollHeight;
      this.lastClientHeight = clientHeight;
    }

    // То же с горизонтальным скроллбаром
    if (isChangedHorizontally) {
      if (this.horizontalScrollbar) {
        this.horizontalScrollbar.render(scrollLeft, scrollTop, scrollWidth, clientWidth);
      }

      this.lastScrollWidth = scrollWidth;
      this.lastClientWidth = clientWidth;
    }

    // Если поменялся overflow элемента, включаем/выключаем обработку скролла
    if (this.wasElementOverflown !== this.isElementOverflown) {
      this.wasElementOverflown = this.isElementOverflown;

      if (this.isElementOverflown) {
        this.setScrollProcessing();
      } else {
        this.removeScrollProcessing();
      }
    }
  }

  /**
   * Функция определяет начальное состояние overflow элемента и устанавливает обработку
   * на scroll. Это - единственное место, где устанавливается обработка на scroll, если
   * опция dynamic === false
   */
  processInitialOverflow() {
    this.wasElementOverflown = this.isElementOverflown;

    if (this.isElementOverflown) {
      this.setScrollProcessing();
    }
  }

  setScrollProcessing() {
    this.element.addEventListener('wheel', this.wheelHandler);
    this.element.addEventListener('touchstart', this.touchstartHandler);
    this.element.addEventListener('touchend', this.touchendHandler);

    this.element.addEventListener('mousedown', this.mouseDownHandler);
    this.element.addEventListener('mouseup', this.mouseUpHandler);
    this.element.addEventListener('mousemove', this.mouseMoveHandler);

    document.addEventListener('touchmove', this.touchmoveHandler, { passive: false });
    document.addEventListener('keydown', this.keydownHandler);
    document.addEventListener('keyup', this.keyupHandler);

    this.element.addEventListener('mouseleave', this.mouseUpHandler);
  }

  removeScrollProcessing() {
    this.element.removeEventListener('wheel', this.wheelHandler);
    this.element.removeEventListener('touchstart', this.touchstartHandler);
    this.element.removeEventListener('touchend', this.touchendHandler);

    document.removeEventListener('touchmove', this.touchmoveHandler);
    document.removeEventListener('keydown', this.keydownHandler);
    document.removeEventListener('keyup', this.keyupHandler);

    this.element.removeEventListener('mousedown', this.mouseDownHandler);
    this.element.removeEventListener('mouseup', this.mouseUpHandler);
    this.element.removeEventListener('mousemove', this.mouseMoveHandler);
  }

  wheelHandler(event) {
    event.stopPropagation();
    event.preventDefault();
    let { deltaY, deltaX } = event;

    if (deltaX === 0 && deltaY !== 0 && this.isShiftBeingPressed) {
      deltaX = deltaY;
    } else if (deltaX === 0 && deltaY !== 0 && this.invertScroll) {
      deltaX = deltaY;
    }

    deltaY = deltaY * this.speed;
    deltaX = deltaX * this.speed;

    this.applyScroll(deltaX, deltaY);
  }

  touchstartHandler(event) {
    this.scrollDeltaSpeed.deltaX = 0;
    this.scrollDeltaSpeed.deltaY = 0;
    this.scrollDeltaSpeed.count = 1;/**/
    this.currentTouch = {
      clientY: event.touches[0].clientY,
      clientX: event.touches[0].clientX,
    };
    this.inertialScroll(0, 0);
    this.isTouchStarted = true;
  }

  mouseDownHandler(event) {
    this.inertialScroll(0, 0);
    this.touchstartHandler({
      touches: [{
        clientY: event.clientY,
        clientX: event.clientX,
      }],
    });
  }

  // eslint-disable-next-line no-unused-vars
  mouseUpHandler(event) {
    this.touchendHandler();
  }

  mouseMoveHandler(event) {
    this.touchmoveHandler({
      preventDefault: event.preventDefault.bind(event),
      changedTouches: [{
        clientY: event.clientY,
        clientX: event.clientX,
      }],
    });
  }

  // eslint-disable-next-line no-unused-vars
  touchendHandler(inertial = false) {
    if (this.isTouchStarted) {
      this.isTouchStarted = false;
      this.inertialScroll(this.scrollDeltaSpeed.deltaX, this.scrollDeltaSpeed.deltaY);
    }
    this.currentTouch = { clientY: 0, clientX: 0 };
  }

  // eslint-disable-next-line complexity
  inertialScroll(deltaX, deltaY) {
    if (this.isTouchStarted) {
      return;
    }
    const deltaRate = 0.99;
    const stopDelta = 0.5;
    if (deltaX || deltaY) {
      window.requestAnimationFrame(() => {
        this.applyScroll(deltaX, deltaY);
        this.inertialScroll(deltaX, deltaY);
      });

      deltaX *= deltaRate;
      deltaY *= deltaRate;

      if (Math.abs(deltaX) < stopDelta) {
        deltaX = 0;
      }
      if (Math.abs(deltaY) < stopDelta) {
        deltaY = 0;
      }
    }

    this.scrollDeltaSpeed.deltaX = deltaX;
    this.scrollDeltaSpeed.deltaY = deltaY;
    this.scrollDeltaSpeed.count = 1;
  }

  averageDeltaSpeed(deltaX, deltaY) {
    this.scrollDeltaSpeed.count ++;
    /*
    * count нужен чтобы подсчитать что-то похожее на среднее арифметическое скоростей за последние N тактов события
    * это нужно чтобы компенсировать неравномерность скорости ведения указателя
    * */
    if (this.scrollDeltaSpeed.count > 5) {
      this.scrollDeltaSpeed.count = 5;
    }
    if (!deltaX && !deltaY) {
      this.scrollDeltaSpeed.deltaX = 0;
      this.scrollDeltaSpeed.deltaY = 0;
    } else {
      const seriesCount = this.scrollDeltaSpeed.count;
      this.scrollDeltaSpeed.deltaX = this.scrollDeltaSpeed.deltaX * ((seriesCount - 1) / seriesCount) + deltaX / seriesCount;
      this.scrollDeltaSpeed.deltaY = this.scrollDeltaSpeed.deltaY * ((seriesCount - 1) / seriesCount) + deltaY / this.scrollDeltaSpeed.count;
    }
  }

  touchmoveHandler(event) {
    if (!this.isTouchStarted) { return; }

    event.preventDefault();

    const { clientY, clientX } = this.currentTouch;
    const { clientY: newClientY, clientX: newClientX } = event.changedTouches[0];
    const deltaY = clientY - newClientY;
    const deltaX = clientX - newClientX;

    this.averageDeltaSpeed(deltaX, deltaY);

    this.applyScroll(deltaX, deltaY);
    this.currentTouch.clientY = newClientY;
    this.currentTouch.clientX = newClientX;
  }

  /**
   * Отслеживает нажатый шифт для горизонтального прокручивания колесиком мыши
   */
  keydownHandler(event) {
    if (event.which === SHIFT_CODE) {
      this.isShiftBeingPressed = true;
    }
  }

  keyupHandler(event) {
    if (event.which === SHIFT_CODE) {
      this.isShiftBeingPressed = false;
    }
  }

  /**
   * Применяет скролл к элементу и вызывает метод на скроллбаре
   */
  applyScroll(deltaX, deltaY) {
    if (this.element.scrollBy) {
      this.element.scrollBy(deltaX, deltaY);
    } else {
      this.element.scrollLeft += deltaX;
      this.element.scrollTop += deltaY;
    }

    this.savedScrollLeft = this.element.scrollLeft;
    this.savedScrollTop = this.element.scrollTop;
    const { scrollTop, scrollLeft } = this.element;

    if (this.verticalScrollbar) {
      this.verticalScrollbar.render(scrollTop, scrollLeft);
    }

    if (this.horizontalScrollbar) {
      this.horizontalScrollbar.render(scrollLeft, scrollTop);
    }
  }
}

export default ScrollbarDirectiveFactory.createHooks();
export { ScrollbarDirectiveFactory };
